import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FSetSnackbarProps, Inputs } from "../../types";

import { Checkbox, CircularProgress, Divider, FormControlLabel, Grid, LinearProgress, MenuItem, TextField, Typography, makeStyles } from "@material-ui/core";
import { Fragment, useEffect, useState } from "react";
import { useSnackbar } from "../../../../contexts/snackbar";
import { useStoragedUserInfo } from "../../../../contexts/user";
import { axiosCallHandler } from "../../../../infra/axiosHelper";
import { retrieveFromStorage, saveToStorage } from "../../../../utils/local-storage-manager";
import * as S from "../components/UserForm/styles";
import { ClippingSourceProps } from "../components/UserForm/types";
import { CheckBoxContainer, SavingScreenLayer } from "./styles";



export function EditUser() {
  const { user, setUser } = useStoragedUserInfo();
  const snackbar = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<Inputs>({ values: {  
    nome: user.name, 
    username: 
    user.username, 
    password1: '', 
    password2: '', 
    email: user.email, 
    enabled: user.enabled ? 'SIM' : 'NÃO', 
    role: user.role, 
    rendering: user.rendering 
  } });

  const [isClippingSourceLoading, setIsClippingSourceLoading] = useState(true);
  const [indeterminate, setIndeterminate] = useState(false);
  const [formUserClippingSourceList, setFormUserClippingSourceList] = useState<Map<string, ClippingSourceProps>>(
    new Map()
  );
  let currentLabel = ''
/**
 * A function that returns boolean
 * @param userFormData is the user data from form
 * @returns boolean value of any user info change
*/
  function isUserInfoDiff(userFormData: Inputs) {
    //adicionar posteriormente as mudanças às checkboxes também
    let isRoleDirty = userFormData.role !== user.role;
    let formActiveState = userFormData.enabled === "SIM";
    let isActiveStateDirty = formActiveState !== user.enabled;
    let isRenderingDirty = userFormData.rendering !== user.rendering

    return isRoleDirty || isActiveStateDirty || isRenderingDirty;
  }

  function setSnackbar({ status, type, message }: FSetSnackbarProps) { 
    snackbar.setStatus(status);
    snackbar.setType(type);
    snackbar.setMessage(message);
  }

  const useStyles = makeStyles((theme) => ({
    formControlLabel: {
      '&.Mui-disabled > span:nth-child(2)': {
        color: '#5f5f5f45'
      }
    },
  }));

  const classes = useStyles();

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    const { username, email, role, rendering, enabled } = data;
    setIsSaving(true);

    //Altera as informações do usuário caso existam mudanças
    if(isUserInfoDiff(data)){
      try {
        updateUserRole(username, role).then((res) => {
          setUser(prev => ({
            ...prev,
            role
          }))
          updateUserActivateStatus(email, enabled).then((res) => {
            setUser(prev => ({
              ...prev,
              enabled: res.enabled
            }))
            updateUserRendering(email, rendering).then((res) => {
              setUser(prev => ({
                ...prev,
                rendering: res.rendering
              }))
            }).catch(err => {
              console.log(err);
              setSnackbar({ status: true, type: 'success', message: 'Houve um erro ao tentar modificar a renderização do usuário' });
            })
          }).catch(err => {
            console.log(err);
            setSnackbar({ status: true, type: 'error', message: 'Houve um erro ao tentar modificar o status do usuário' });
          })
        })
      }catch(e) {
        setSnackbar({ status: true, type: 'success', message: 'Houve um erro ao tentar alterar as informações do usuário' });
        console.log(e)
      }
    }

    const listOfChangedSources = getFormUserClippingSourceAsArrayOfKeys().filter(key => {
      const element = formUserClippingSourceList.get(key);

      return element?.changed
    });

    //Altera o estado de checked das praças do usuário caso haja alteração
    new Promise((resolve, reject) => {
      if(listOfChangedSources.length === 0) {
        setTimeout(() => {
          resolve({  finished: true })
        }, 1000)
      }

      listOfChangedSources.forEach((key, idx) => {
        const element = formUserClippingSourceList.get(key)
  
        if (element?.checked && element?.changed) {
          axiosCallHandler({
            method: 'POST',
            url: `/users/${user.email}/groups/${element?.name}/bind`,
          })
          .then(() => {
            if(idx === listOfChangedSources.length - 1) {
              resolve({  finished: true });
            }
          })
            .catch((err) => {
              setSnackbar({ status: true, type: 'error', message: 'Houve um erro ao tentar salvar suas alterações' });
              reject('Houve um erro ao tentar remover uma das opções');
            });
        }
  
        if (!element?.checked && element?.changed) {
          axiosCallHandler({
            method: 'POST',
            url: `/users/${user.email}/groups/${element?.name}/unbind`,
          })
          .then(() => {
            if(idx === listOfChangedSources.length - 1) {
              resolve({  finished: true });
            }
          })
            .catch((err) => {
              setSnackbar({ status: true, type: 'error', message: 'Houve um erro ao tentar remover uma das opções' });
              reject('Houve um erro ao tentar remover uma das opções')
            });
        }
      });
    }).then(res => {
      setIsSaving(false);
      updateUserGroupListInLocalStorage();
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setSnackbar({ status: true, type: 'success', message: 'Alterações realizadas com sucesso!' });
    }).catch(err => {
      setSnackbar({ status: true, type: 'error', message: err });
    })
  }

  function updateUserGroupListInLocalStorage() {
    const newGroupToBeAddedToUser = getFormUserClippingSourceAsArrayOfKeys().filter(key => {
      const element = formUserClippingSourceList.get(key);

      return element?.checked;
    })

    let currentStoragedUser = JSON.parse(retrieveFromStorage("lastEditedUser") as string);
    currentStoragedUser.groups = newGroupToBeAddedToUser;
    saveToStorage("lastEditedUser", currentStoragedUser)
    console.log(newGroupToBeAddedToUser)
  }

  function updateUserRole(username: string, role: 'ROLE_USER' | 'ROLE_ADMIN') {
    return axiosCallHandler({
      method: 'PATCH',
      url: `/users-auth/${username}/role/${role}`,
    })
  }

  function updateUserActivateStatus(email: string, activeStatus: string) {
    const userActiveStatus = activeStatus === 'SIM' ? 'enable' : 'disable';

    return axiosCallHandler({
      method: 'PATCH',
      url: `/users/${email}/${userActiveStatus}`,
    });
  }

  function updateUserRendering(email: string, rendering: 'VIDEO' | 'AUDIO' | 'SIMULCAST') {
    return axiosCallHandler({
      method: 'PATCH',
      url: `/users/${email}/rendering/${rendering}`
    });
  }

  const handleChangeCheckbox = (event: any) => {
    const { value: name, checked } = event.target;
    setIndeterminate(false);

    let clipSource = formUserClippingSourceList.get(name);
    let newMap = new Map(formUserClippingSourceList);

    if(clipSource) {
      newMap.set(
        name,
        {
          ...clipSource,
          checked: checked,
          changed: true
        }
      )
    }

    setFormUserClippingSourceList(newMap)
  };

  function generateClippingSourceMapList(clipList: ClippingSourceProps[]) {
    let clipMapList = new Map<string, ClippingSourceProps>();

    for (let index = 0; index < clipList.length; index++) {
      let element = clipList[index];
      element.checked = false;
      element.changed = false;
      element.label = `${element.location_label} - ${element.channel_label}`

      clipMapList.set(element.name, element);
    }

    return clipMapList
  }

  const getIndeterminatedCheckedStatus = () => {
    //Verifica se todos os items da lista de source estão checked
    const isChecked = getFormUserClippingSourceAsArrayOfKeys().every(key => {
      const keyElement = formUserClippingSourceList.get(key);

      return keyElement?.checked === true || keyElement?.enabled === false
    })

    return isChecked;
  };

  const getIndeterminatedStatus = () => {
    let hasChecked = false;
    let hasNotChecked = false;

    getFormUserClippingSourceAsArrayOfKeys().some(key => {
      const element = formUserClippingSourceList.get(key);
      if(!element?.enabled) return null;

      if(element?.checked){
        hasChecked = true;
      }else{
        hasNotChecked = true;
      }

      return null;
    })

    return hasChecked && hasNotChecked;
  };

  const handleIndeterminateCheckbox = (event: any) => {
    const newClipMap = new Map(formUserClippingSourceList);

    getFormUserClippingSourceAsArrayOfKeys().forEach(key => {
      const element = formUserClippingSourceList.get(key);
      
      if(element){
        const isEnabled = element.enabled;

        newClipMap.set(key, {
          ...element,
          checked: isEnabled ? event.target.checked : false,
          changed: isEnabled ? true : false,
          enabled: isEnabled
        })
      }
    })

    setIndeterminate(event.target.checked);
    setFormUserClippingSourceList(newClipMap)
  };

  function getFormUserClippingSourceAsArrayOfKeys() {
    return [...formUserClippingSourceList.keys()];
  }

  useEffect(() => {

    axiosCallHandler({ method: 'GET', url: '/clipping-sources' }).then(
      (clippingSourceList: ClippingSourceProps[]) => {
        saveToStorage("clippingSourceList", clippingSourceList);

        let newMappedList = generateClippingSourceMapList(clippingSourceList);

        user.groups.forEach((clipName) => {
          let currentClip = newMappedList.get(clipName);
          
          if(currentClip) {
            newMappedList.set(clipName, {
              ...currentClip,
              name: clipName,
              label: `${currentClip.location_label} - ${currentClip.channel_label}`,
              checked: true,
              changed: false,
            });
          }
        });

        let sortedMapList = new Map([...newMappedList].sort(([k, v], [k2, v2]) => {
          if (v.location_label[0] > v2.location_label[0]) {
            return 1;
          }
          if (v.location_label[0] < v2.location_label[0]) {
            return -1;
          }
          return 0; 
        }));
        
        setFormUserClippingSourceList(sortedMapList)
        setIsClippingSourceLoading(false);
      }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <S.Container>
      {isSaving && 
        (
          <SavingScreenLayer>
            <div>
              <CircularProgress size={65} color="primary" />
              <Typography variant="h5" >Salvando alterações</Typography>
            </div>
          </SavingScreenLayer>
        )
      }
      
      <form onSubmit={handleSubmit(onSubmit)}>
        <S.NameEmailWrapper>
          <Controller
            name="nome"
            control={control}
            render={({ field }) => (
              <S.StyledTextField
                variant="outlined"
                label="Nome"
                $hasError={errors.nome}
                $isDisabled
                InputLabelProps={{ shrink: true }}
                {...field}
              />
            )} 
          />

          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <S.StyledTextField
                variant="outlined"
                label="Email"
                $hasError={errors.email}
                $isDisabled={true}
                InputLabelProps={{ shrink: true }}
                {...field}
              />
            )} 
          />
        </S.NameEmailWrapper>
        <Divider />
        <S.UserInfoWrapper>
          <Controller
            name="username"
            control={control}
            render={({ field }) => (
              <S.StyledTextField
                $gridArea={field.name}
                variant="outlined"
                label="Usuário"
                $hasError={errors.username}
                $isDisabled={true}
                InputLabelProps={{ shrink: true }}
                {...field}
              />
            )} 
          /> 
          <Controller
            name="password1"
            control={control}
            render={({ field }) => (
              <S.StyledTextField
                $gridArea={field.name}
                variant="outlined"
                label="Senha"
                $hasError={errors.password1}
                $isDisabled={true}
                InputLabelProps={{ shrink: true }}
                {...field}
              />
            )} 
          />
          <Controller
            name="password2"
            control={control}
            render={({ field }) => (
              <S.StyledTextField
                $gridArea={field.name}
                variant="outlined"
                label="Confirmar senha"
                $hasError={errors.password2}
                $isDisabled={true}
                InputLabelProps={{ shrink: true }}
                {...field}
              />
            )} 
          />  
        </S.UserInfoWrapper>
        <Divider />
        <S.UserStateWrapper>
          <Controller
            name="role"
            control={control}
            rules={{ required: true}}
            defaultValue="ROLE_USER"
            render={({ field }) => (
              <TextField
                select
                label="Cargo"
                variant="outlined"
                {...field}
              >
                <MenuItem
                  key="ROLE_USER"
                  value="ROLE_USER"
                >
                  USER
                </MenuItem>
                <MenuItem
                  key="ROLE_ADMIN"
                  value="ROLE_ADMIN"
                >
                  ADMIN
                </MenuItem>
              </TextField>
            )}
          />

          <Controller
            name="enabled"
            control={control}
            defaultValue="SIM"
            render={({ field }) => (
              <TextField
                select
                label="Ativo"
                variant="outlined"
                {...field}
              >
                <MenuItem
                  key="ACTIVE"
                  value="SIM"
                >
                  SIM
                </MenuItem>
                <MenuItem
                  key="INACTIVE"
                  value="NÃO"
                >
                  NÃO
                </MenuItem>
              </TextField>
            )} 
          />
          
          <Controller
            name="rendering"
            control={control}
            defaultValue="VIDEO"
            render={({ field }) => (
              <TextField
                select
                label="Rendering"
                variant="outlined"
                {...field}
              >
                <MenuItem
                  key="VIDEO"
                  value="VIDEO"
                >
                  Vídeo
                </MenuItem>
                <MenuItem
                  key="AUDIO"
                  value="AUDIO"
                >
                  Audio
                </MenuItem>
                <MenuItem
                  key="SIMULCAST"
                  value="SIMULCAST"
                >
                  Simulcast
                </MenuItem>
              </TextField>
            )} 
          />
          
          
        </S.UserStateWrapper>
        <Divider />

        
        {
          isClippingSourceLoading
          ?
          (
            <LinearProgress />
          )
          :
          (
            <Fragment>
              <FormControlLabel
                label="Selecionar todos"
                name="select-all"
                value={indeterminate}
                control={
                  <Checkbox
                    name="select-all"
                    color="primary"
                    checked={getIndeterminatedCheckedStatus()}
                    indeterminate={getIndeterminatedStatus()}
                  />
                }
                onChange={(event: any) => handleIndeterminateCheckbox(event)}
              />
              <CheckBoxContainer container spacing={2}>
                {
                  getFormUserClippingSourceAsArrayOfKeys().map(item => {
                    let mapCurrentLabel = formUserClippingSourceList.get(item)?.location_label[0].toUpperCase();
                    let element = formUserClippingSourceList.get(item);
                    
                    if(mapCurrentLabel !== currentLabel){
                      currentLabel = mapCurrentLabel ?? '';
                      
                      return (
                        <Fragment key={element?.name}>
                          <Grid item lg={12} md={12} xs={12}>
                            <Typography style={{ fontSize: 55, marginTop: 25 }}>{mapCurrentLabel}</Typography>
                          </Grid>
                          <Grid item md={4} sm={6} xs={12} lg={3}>
                            <FormControlLabel
                              className={classes.formControlLabel}
                              key={element?.name}
                              name={element?.label}
                              checked={element?.checked && element?.enabled}
                              label={element?.label}
                              value={element?.name}
                              disabled={!element?.enabled}
                              control={
                                <Checkbox name={element?.label} color="primary" id={element?.name} />
                              }
                              onChange={(event: any) => handleChangeCheckbox(event)}
                            />
                          </Grid>
                        </Fragment>
                      )
                    }
                    
                    return (
                      <Grid item md={4} sm={6} xs={12} lg={3} key={element?.name}>
                        <FormControlLabel
                          // className="checkbox"
                          className={classes.formControlLabel}
                          name={element?.label}
                          checked={element?.checked && element?.enabled}
                          label={element?.label}
                          value={element?.name}
                          disabled={!element?.enabled}
                          control={
                            <Checkbox name={element?.label} color="primary" id={element?.name} key={element?.name} />
                          }
                          onChange={(event: any) => handleChangeCheckbox(event)}
                          />
                      </Grid>
                    )
                  })
                }
              </CheckBoxContainer>
              <S.StyledButton type="submit">Salvar</S.StyledButton>
            </Fragment>
          )
        }
      </form>
    </S.Container>
  );
};